// Details Modal Web Component
// Handles modal dialogs with focus trapping and escape key handling
// Depends on global trapFocus/removeTrapFocus from vendor/global.js

import { onVendorReady } from '@/utils/vendor-ready'

class DetailsModal extends HTMLElement {
  private detailsContainer: HTMLDetailsElement
  private summaryToggle: HTMLElement
  private onBodyClickEvent: ((event: MouseEvent) => void) | null = null

  constructor() {
    super()
    this.detailsContainer = this.querySelector('details')!
    this.summaryToggle = this.querySelector('summary')!

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.close()
      }
    })

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this))

    const closeButton = this.querySelector('button[type="button"]')
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close())
    }

    this.summaryToggle.setAttribute('role', 'button')
  }

  isOpen(): boolean {
    return this.detailsContainer.hasAttribute('open')
  }

  private onSummaryClick(event: MouseEvent): void {
    event.preventDefault()
    const details = (event.target as HTMLElement).closest('details')!
    details.hasAttribute('open') ? this.close() : this.open(event)
  }

  private onBodyClick(event: MouseEvent): void {
    const target = event.target as HTMLElement
    if (!this.contains(target) || target.classList.contains('modal-overlay')) {
      this.close(false)
    }
  }

  open(event: MouseEvent): void {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this)
    ;(event.target as HTMLElement).closest('details')!.setAttribute('open', 'true')
    document.body.addEventListener('click', this.onBodyClickEvent)
    document.body.classList.add('overflow-hidden')

    // Trap focus using global function from global.js
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]') as HTMLElement,
        this.detailsContainer.querySelector('input:not([type="hidden"])') as HTMLElement
      )
    }
  }

  close(focusToggle = true): void {
    // Remove focus trap using global function from global.js
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(focusToggle ? this.summaryToggle : null)
    }

    this.detailsContainer.removeAttribute('open')
    if (this.onBodyClickEvent) {
      document.body.removeEventListener('click', this.onBodyClickEvent)
    }
    document.body.classList.remove('overflow-hidden')
  }
}

// Register custom element only after vendor is ready
// This ensures trapFocus/removeTrapFocus are available
onVendorReady(() => {
  if (!customElements.get('details-modal')) {
    customElements.define('details-modal', DetailsModal)
  }
})

export default DetailsModal
